import React, { useEffect, useMemo } from 'react';
import EmojiPopupSkinTonePicker from './EmojiPopupSkinTonePicker';
import PopupModeMenu from '../../../popupPanel/types/modeMenu/PopupModeMenu';
import { CATEGORIES } from '../emojiPopupConstants';
import { Emoji, EmojisRowData } from '../../emojiTypes';
import { isTitleRow } from '../emojiPopupUtil';
import { translateCategory } from '../emojiCategoryTranslations';

// Components

import { useTranslator } from "../../../i18n/useTranslator";

type EmojiPopupMenuProps = {
    emojiData: Emoji[];
    skinTone: string;
    currentCategory: string;
    setCurrentCategory: (category: string) => void;
    setSearchFilter: (filter: string) => void;
    gridSize: number;
    emojisRowData: EmojisRowData;
    listRef: any;
    dispatchUpdateSkinTone: (skinTone: string) => void;
};

const EmojiPopupMenu = ({
    emojiData = [],
    skinTone,
    currentCategory,
    setCurrentCategory,
    setSearchFilter,
    gridSize,
    emojisRowData,
    listRef,
    dispatchUpdateSkinTone,
}: EmojiPopupMenuProps) => {
    const translate = useTranslator('popup', 'emoji.menu');

    const setEmojiCategory = ({ mode }: { mode: string }) => {
        setCurrentCategory(mode);

        const scrollToRowIndex = emojisRowData.findIndex((row) => isTitleRow(row) && row.title === mode);

        if (scrollToRowIndex > -1 && listRef.current) {
            listRef.current.scrollToItem(scrollToRowIndex, 'start');
        }
    };

    const pickerModeList = useMemo(
        () =>
            CATEGORIES.map((category) => ({
                slug: category.name,
                title: translateCategory(category.name, translate),
                disabled: !emojisRowData.find((row) => isTitleRow(row) && row.title === category.name),
            })),
        [emojisRowData, translate],
    );

    useEffect(() => {
        const currentMode = pickerModeList.find((mode) => mode && mode.slug === currentCategory);

        if (currentMode && !currentMode.disabled) return;

        const firstEnabledMode = pickerModeList.find((mode) => mode && !mode.disabled);
        setCurrentCategory((firstEnabledMode && firstEnabledMode.slug) || 'none');
    }, [pickerModeList]);

    return (
        <PopupModeMenu
            tools={
                <EmojiPopupSkinTonePicker
                    emojiData={emojiData}
                    skinTone={skinTone}
                    setSkinTone={dispatchUpdateSkinTone}
                    gridSize={gridSize}
                />
            }
            showSearch
            popupMode={currentCategory}
            setMode={setEmojiCategory}
            modeList={pickerModeList}
            searchPlaceholder={translate('searchPlaceholder') + '...'}
            clearSearchOnSubmit={false}
            handleSearchChange={setSearchFilter}
        />
    );
};

export default EmojiPopupMenu;
